import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import classes from './Responsive.module.scss';

interface Device {
  label: string;
  width: number;
  height: number;
  proxyPort: number;
}

const DEVICES: Device[] = [
  { label: 'Android', width: 360, height: 800, proxyPort: 4001 },
  { label: 'iPhone SE', width: 375, height: 667, proxyPort: 4002 },
  { label: 'iPhone', width: 390, height: 844, proxyPort: 4003 },
  { label: 'iPad', width: 768, height: 1024, proxyPort: 4004 },
  { label: 'iPad Landscape', width: 1024, height: 768, proxyPort: 4005 },
  { label: 'Laptop', width: 1280, height: 800, proxyPort: 4006 },
  { label: 'Desktop', width: 1440, height: 900, proxyPort: 4007 },
  { label: 'Large Desktop', width: 1920, height: 1080, proxyPort: 4008 },
];

const PANEL_PADDING = 24;

export const Responsive: FunctionComponent = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const [inputValue, setInputValue] = useState('/');

  const containerRef = useRef<HTMLDivElement>(null);

  const iframeRefs = useRef<(HTMLIFrameElement | null)[]>([]);

  const scaleActiveIframe = useCallback(() => {
    const container = containerRef.current;
    const iframe = iframeRefs.current[activeIndex];

    if (!container || !iframe) {
      return;
    }

    const device = DEVICES[activeIndex];
    const width = container.clientWidth - PANEL_PADDING * 2;
    const height = container.clientHeight - PANEL_PADDING * 2;
    const scale = Math.min(width / device.width, height / device.height, 1);
    const left = (container.clientWidth - device.width * scale) / 2;

    iframe.style.width = `${device.width}px`;
    iframe.style.height = `${device.height}px`;
    iframe.style.transform = `scale(${scale})`;
    iframe.style.transformOrigin = 'top left';
    iframe.style.position = 'absolute';
    iframe.style.left = `${left}px`;
    iframe.style.top = `${PANEL_PADDING}px`;
  }, [activeIndex]);

  useEffect(() => {
    scaleActiveIframe();

    const observer = new ResizeObserver(scaleActiveIframe);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [scaleActiveIframe]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      setInputValue(new URL(inputValue, 'http://x').pathname);
    },
    [inputValue]
  );

  return (
    <div className={classes['page']}>
      <form className={classes['toolbar']} onSubmit={handleSubmit}>
        <input
          className={classes['toolbar__input']}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <button className={classes['toolbar__btn']} type={'submit'}>
          Go
        </button>
      </form>

      <div className={classes['tabs']}>
        {DEVICES.map(({ label, width }, i) => (
          <button
            key={label}
            className={classNames(classes['tab'], { [classes['tab--active']]: i === activeIndex })}
            onClick={() => setActiveIndex(i)}
          >
            {label}
            <span className={classes['tab__size']}>{width}px</span>
          </button>
        ))}
      </div>

      <div ref={containerRef} className={classes['container']}>
        {DEVICES.map(({ label, proxyPort }, i) => (
          <div
            key={label}
            className={classNames(classes['container__panel'], {
              [classes['container__panel--active']]: i === activeIndex,
            })}
          >
            <iframe
              ref={(el) => (iframeRefs.current[i] = el)}
              src={`http://localhost:${proxyPort}${inputValue}`}
              title={label}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
